import React, { useState, useMemo, useCallback, useEffect } from "react";
import "./Auditlog.scss";
import { getAuditLogsApi, getAuditLogByIdApi } from "../../api/api";

// ─── Module config ────────────────────────────────────────────────────────────
const MODULE_MAP = {
  Users: { color: "#0062FE", bg: "rgba(0,98,254,0.10)" },
  Messages: { color: "#8B5CF6", bg: "rgba(139,92,246,0.10)" },
  WhatsApp: { color: "#22C55E", bg: "rgba(34,197,94,0.10)" },
  Security: { color: "#EF4444", bg: "rgba(239,68,68,0.10)" },
  Rooms: { color: "#8B5CF6", bg: "rgba(139,92,246,0.10)" },
  System: { color: "#F97316", bg: "rgba(249,115,22,0.10)" },
  Audit: { color: "#06B6D4", bg: "rgba(6,182,212,0.10)" },
  Media: { color: "#F59E0B", bg: "rgba(245,158,11,0.10)" },
  Broadcasts: { color: "#0062FE", bg: "rgba(0,98,254,0.10)" },
  USER: { color: "#0062FE", bg: "rgba(0,98,254,0.10)" },
  GROUP: { color: "#8B5CF6", bg: "rgba(139,92,246,0.10)" },
  ROLE: { color: "#EF4444", bg: "rgba(239,68,68,0.10)" },
  BROADCAST: { color: "#0062FE", bg: "rgba(0,98,254,0.10)" },
  WHATSAPP: { color: "#22C55E", bg: "rgba(34,197,94,0.10)" },
  MEDIA: { color: "#F59E0B", bg: "rgba(245,158,11,0.10)" },
  AUTH: { color: "#EF4444", bg: "rgba(239,68,68,0.10)" },
  DEPARTMENT: { color: "#06B6D4", bg: "rgba(6,182,212,0.10)" },
};

// ─── Map API action strings → severity ───────────────────────────────────────
const ACTION_SEVERITY_MAP = {
  // success
  USER_CREATED: "success",
  GROUP_CREATED: "success",
  ROOM_CREATED: "success",
  BROADCAST_SENT: "success",
  MEMBER_ADDED: "success",
  DEPARTMENT_CREATED: "success",
  ROLE_CREATED: "success",
  // info
  FILE_SHARED: "info",
  WHATSAPP_ROUTED: "info",
  AUDIT_EXPORTED: "info",
  BROADCAST_CREATED: "info",
  MEDIA_UPLOADED: "info",
  // warning
  ROLE_CHANGED: "warning",
  PERMISSION_CHANGED: "warning",
  PASSWORD_RESET: "warning",
  SESSION_RESET: "warning",
  USER_DEACTIVATED: "warning",
  GROUP_ARCHIVED: "warning",
  USER_UPDATED: "warning",
  GROUP_UPDATED: "warning",
  // critical
  LOGIN_FAILED: "critical",
  MEDIA_DELETED: "critical",
  SYSTEM_RESTART: "critical",
  IP_ALLOWLIST_CHANGED: "critical",
  USER_DELETED: "critical",
  ROLE_DELETED: "critical",
};

const SEVERITY_LABEL = {
  info: "Info",
  success: "Success",
  warning: "Warning",
  critical: "Critical",
};

// ─── Normalize API log → component shape ─────────────────────────────────────
function normalizeLog(log) {
  const action = log.action || "";
  const severity = ACTION_SEVERITY_MAP[action] || "info";

  // Format readable action label: "USER_CREATED" → "User Created"
  const actionLabel = action
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

  // Format time
  const createdAt = log.createdAt ? new Date(log.createdAt) : null;
  const timeLabel = createdAt
    ? createdAt.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

  // Derive module from entityType
  const entityType = (log.entityType || "System").toUpperCase();
  const module =
    {
      USER: "USER",
      GROUP: "GROUP",
      ROLE: "ROLE",
      BROADCAST: "BROADCAST",
      WHATSAPP: "WHATSAPP",
      MEDIA: "MEDIA",
      AUTH: "AUTH",
      DEPARTMENT: "DEPARTMENT",
    }[entityType] || "System";

  return {
    id: log.id || "—",
    actor: log.actor?.fullName || log.actorUserId || "System",
    actorRole: log.actor?.email || "",
    actorStatus: log.actor?.status || "",
    action: actionLabel,
    actionRaw: action,
    target: log.entityId || "—",
    targetType: log.entityType || "—",
    module,
    sourceIp: log.actor?.mobile || "N/A",
    source: "Web Admin",
    time: timeLabel,
    timestamp: log.createdAt || "",
    severity,
    before: null,
    after:
      log.details && Object.keys(log.details).length > 0 ? log.details : null,
  };
}

const ACTOR_COLORS = [
  "#0062FE",
  "#8B5CF6",
  "#22C55E",
  "#F97316",
  "#EF4444",
  "#06B6D4",
  "#F59E0B",
  "#6366F1",
];

function getActorColor(name) {
  if (!name) return "#0062FE";
  let hash = 0;
  for (let i = 0; i < name.length; i++)
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return ACTOR_COLORS[Math.abs(hash) % ACTOR_COLORS.length];
}

const PAGE_SIZE_OPTS = [10, 25, 50];

function initials(n) {
  return (n || "?")
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

// ─── Icons ───────────────────────────────────────────────────────────────────
const Ico = {
  Search: (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  Filter: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  ),
  Export: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  Close: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  ChevD: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  ChevR: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  SortUp: (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="18 15 12 9 6 15" />
    </svg>
  ),
  Copy: (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  ),
  Shield: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  Activity: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  AlertTri: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  Lock: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  XCircle: (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  ),
  Refresh: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  ),
  Eye: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  Clock: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  User: (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Globe: (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  Loader: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="al-spin"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  ),
};

// ─── Before/After Diff View ───────────────────────────────────────────────────
function DiffView({ before, after }) {
  const [tab, setTab] = useState("after");
  const data = tab === "before" ? before : after;
  if (!before && !after) return null;
  return (
    <div className="al-diff">
      <div className="al-diff__header">
        {before && (
          <button
            className={tab === "before" ? "active" : ""}
            onClick={() => setTab("before")}
          >
            Before
          </button>
        )}
        {after && (
          <button
            className={tab === "after" ? "active" : ""}
            onClick={() => setTab("after")}
          >
            After
          </button>
        )}
      </div>
      <div className="al-diff__body">
        {data &&
          Object.entries(data).map(([k, v]) => {
            const changed =
              before &&
              after &&
              JSON.stringify(before[k]) !== JSON.stringify(after[k]);
            const isAdded = !before?.[k] && after?.[k] !== undefined;
            return (
              <div
                key={k}
                className={`al-diff__line${changed && tab === "after" ? " al-diff__line--added" : changed && tab === "before" ? " al-diff__line--removed" : isAdded ? " al-diff__line--added" : " al-diff__line--neutral"}`}
              >
                <span className="al-diff__line--key">"{k}"</span>:{" "}
                <span className="al-diff__line--val">"{String(v)}"</span>
                {changed && tab === "after" && (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 10,
                      color: "#86EFAC",
                      opacity: 0.8,
                    }}
                  >
                    ← changed
                  </span>
                )}
                {changed && tab === "before" && (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 10,
                      color: "#FCA5A5",
                      opacity: 0.8,
                    }}
                  >
                    ← was
                  </span>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}

// ─── Detail Drawer ────────────────────────────────────────────────────────────
function DetailDrawer({ event: ev, onClose, onCopy }) {
  const [fullLog, setFullLog] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (!ev?.id) return;
    setLoadingDetail(true);
    getAuditLogByIdApi(ev.id)
      .then((res) => {
        if (res.data?.success && res.data?.log) {
          setFullLog(normalizeLog(res.data.log));
        }
      })
      .catch(() => {
        // fallback to row data if single fetch fails
        setFullLog(ev);
      })
      .finally(() => setLoadingDetail(false));
  }, [ev?.id]);

  const display = fullLog || ev;

  const mod = MODULE_MAP[display.module] || MODULE_MAP.System;
  const sev = display.severity;
  const sevColors = {
    info: { color: "#0062FE", bg: "rgba(0,98,254,0.12)" },
    success: { color: "#22C55E", bg: "rgba(34,197,94,0.12)" },
    warning: { color: "#F97316", bg: "rgba(249,115,22,0.12)" },
    critical: { color: "#EF4444", bg: "rgba(239,68,68,0.12)" },
  }[sev] || { color: "#0062FE", bg: "rgba(0,98,254,0.12)" };

  const heroBg =
    sev === "info"
      ? "#0062FE"
      : sev === "success"
        ? "#059669"
        : sev === "warning"
          ? "#D97706"
          : "#DC2626";

  const copy = (txt) => {
    navigator.clipboard?.writeText(txt).catch(() => {});
    onCopy(txt);
  };

  return (
    <>
      <div className="al-drawer-overlay" onClick={onClose} />
      <div className="al-drawer">
        <div className="al-drawer__header">
          <div>
            <div
              className="al-drawer__event-id"
              onClick={() => copy(display.id)}
              title="Click to copy"
              style={{ cursor: "pointer" }}
            >
              {display.id} {Ico.Copy}
            </div>
            <h3 className="al-drawer__title">Audit Event Detail</h3>
            <p className="al-drawer__subtitle">
              {display.time} · Read-only record
            </p>
          </div>
          <button className="al-drawer__close" onClick={onClose}>
            {Ico.Close}
          </button>
        </div>

        <div className="al-drawer__body">
          {loadingDetail ? (
            <div className="al-loading-inline">
              {Ico.Loader} Loading full details…
            </div>
          ) : (
            <>
              {/* Hero */}
              <div className="al-event-hero" style={{ background: heroBg }}>
                <div
                  className="al-event-hero__icon"
                  style={{
                    background: "rgba(255,255,255,0.15)",
                    color: "#fff",
                  }}
                >
                  {sev === "critical" || sev === "warning"
                    ? Ico.AlertTri
                    : Ico.Shield}
                </div>
                <div className="al-event-hero__info">
                  <div className="al-event-hero__action">{display.action}</div>
                  <div className="al-event-hero__meta">
                    <span>
                      {Ico.User} {display.actor}
                    </span>
                    <span>
                      {Ico.Clock} {display.time}
                    </span>
                    <span>
                      {Ico.Globe} {display.sourceIp}
                    </span>
                  </div>
                </div>
                <span
                  className={`al-severity al-severity--${sev}`}
                  style={{ background: "rgba(255,255,255,0.2)", color: "#fff" }}
                >
                  {SEVERITY_LABEL[sev]}
                </span>
              </div>

              {/* Core metadata */}
              <div className="al-detail-section">
                <span className="al-detail-section__title">Event Metadata</span>
                <div className="al-detail-grid">
                  <div
                    className="al-detail-field al-detail-field--copy"
                    onClick={() => copy(display.id)}
                  >
                    <div className="al-detail-field__label">Event ID</div>
                    <div className="al-detail-field__mono">{display.id}</div>
                  </div>
                  <div className="al-detail-field">
                    <div className="al-detail-field__label">Timestamp</div>
                    <div className="al-detail-field__value">{display.time}</div>
                  </div>
                  <div className="al-detail-field">
                    <div className="al-detail-field__label">Actor</div>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <div
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          background: getActorColor(display.actor),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 9,
                          fontWeight: 700,
                          color: "#fff",
                        }}
                      >
                        {initials(display.actor)}
                      </div>
                      <div>
                        <div className="al-detail-field__value">
                          {display.actor}
                        </div>
                        <div style={{ fontSize: 11, color: "#6B7280" }}>
                          {display.actorRole}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="al-detail-field">
                    <div className="al-detail-field__label">Module</div>
                    <div>
                      <span
                        className="al-module"
                        style={{ background: mod.bg, color: mod.color }}
                      >
                        {display.module}
                      </span>
                    </div>
                  </div>
                  <div className="al-detail-field">
                    <div className="al-detail-field__label">Action</div>
                    <div className="al-detail-field__value">
                      {display.action}
                    </div>
                  </div>
                  <div className="al-detail-field">
                    <div className="al-detail-field__label">Severity</div>
                    <div>
                      <span className={`al-severity al-severity--${sev}`}>
                        {SEVERITY_LABEL[sev]}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Target */}
              <div className="al-detail-section">
                <span className="al-detail-section__title">Target</span>
                <div className="al-detail-grid">
                  <div className="al-detail-field">
                    <div className="al-detail-field__label">Entity ID</div>
                    <div
                      className="al-detail-field__mono"
                      style={{ fontSize: 12 }}
                    >
                      {display.target}
                    </div>
                  </div>
                  <div className="al-detail-field">
                    <div className="al-detail-field__label">Entity Type</div>
                    <div className="al-detail-field__value">
                      {display.targetType}
                    </div>
                  </div>
                </div>
              </div>

              {/* Source */}
              <div className="al-detail-section">
                <span className="al-detail-section__title">Source</span>
                <div className="al-detail-grid">
                  <div
                    className="al-detail-field al-detail-field--copy"
                    onClick={() => copy(display.sourceIp)}
                  >
                    <div className="al-detail-field__label">IP / Mobile</div>
                    <span className="al-source">{display.sourceIp}</span>
                  </div>
                  <div className="al-detail-field">
                    <div className="al-detail-field__label">
                      Source Application
                    </div>
                    <div className="al-detail-field__value">
                      {display.source}
                    </div>
                  </div>
                </div>
              </div>

              {/* Before/After diff */}
              {(display.before || display.after) && (
                <div className="al-detail-section">
                  <span className="al-detail-section__title">
                    Event Details
                  </span>
                  <DiffView before={display.before} after={display.after} />
                </div>
              )}

              {/* Immutability notice */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  background: "rgba(0,98,254,0.05)",
                  border: "1px solid rgba(0,98,254,0.12)",
                  borderRadius: 8,
                  padding: "10px 14px",
                  fontSize: 12.5,
                  color: "#374151",
                  marginTop: 4,
                }}
              >
                {Ico.Lock}
                <span>
                  Audit records are <strong>immutable</strong>. This event
                  cannot be edited or deleted from the admin portal.
                </span>
              </div>
            </>
          )}
        </div>

        <div className="al-drawer__footer">
          <button
            className="al-btn al-btn--secondary"
            style={{ flex: 1 }}
            onClick={() => copy(display.id)}
          >
            {Ico.Copy} Copy Event ID
          </button>
          <button
            className="al-btn al-btn--primary"
            onClick={() => alert(`Exporting PDF for ${display.id}…`)}
          >
            {Ico.Export} Export PDF
          </button>
          <button className="al-btn al-btn--ghost" onClick={onClose}>
            {Ico.Close}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Skeleton Row ─────────────────────────────────────────────────────────────
function SkeletonRows({ count = 8 }) {
  return Array.from({ length: count }).map((_, i) => (
    <tr key={i} className="al-skeleton-row">
      {Array.from({ length: 8 }).map((__, j) => (
        <td key={j}>
          <div
            className="al-skeleton"
            style={{
              width: j === 0 ? 120 : j === 2 ? 140 : j === 3 ? 110 : 80,
              height: 14,
            }}
          />
        </td>
      ))}
    </tr>
  ));
}

// ─── Main Component ───────────────────────────────────────────────────────────
const Auditlog = () => {
  // ── API state ──────────────────────────────────────────────────────────────
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // ── UI state ───────────────────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [showAdvanced, setShowAdv] = useState(false);
  const [filterModule, setFMod] = useState("All Modules");
  const [filterSev, setFSev] = useState("All Severity");
  const [filterSource, setFSrc] = useState("All Sources");
  const [filterAction, setFAct] = useState("All Actions");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortKey, setSortKey] = useState("timestamp");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [detail, setDetail] = useState(null);
  const [copyToast, setCopyToast] = useState(null);

  // ── Fetch logs ─────────────────────────────────────────────────────────────
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setApiError(null);
    try {
      const res = await getAuditLogsApi(page, pageSize);
      const { logs = [], pagination = {} } = res.data;
      setEvents(logs.map(normalizeLog));
      setTotalCount(pagination.total || logs.length);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to load audit logs.";
      setApiError(msg);
      setEvents([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // ── Derived filter options from fetched data ───────────────────────────────
  const MODULES_OPT = useMemo(() => {
    const unique = [...new Set(events.map((e) => e.module))].filter(Boolean);
    return ["All Modules", ...unique];
  }, [events]);

  const ACTIONS_OPT = useMemo(() => {
    const unique = [...new Set(events.map((e) => e.action))].filter(Boolean);
    return ["All Actions", ...unique];
  }, [events]);

  const SEVERITIES = ["All Severity", "info", "success", "warning", "critical"];
  const SOURCES = [
    "All Sources",
    "Web Admin",
    "Mobile App",
    "Webhook",
    "External",
    "N/A",
  ];

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = useMemo(
    () => ({
      total: totalCount,
      critical: events.filter((e) => e.severity === "critical").length,
      warning: events.filter((e) => e.severity === "warning").length,
      modules: new Set(events.map((e) => e.module)).size,
    }),
    [events, totalCount],
  );

  // ── Active filters ─────────────────────────────────────────────────────────
  const activeFilters = useMemo(() => {
    const f = [];
    if (filterModule !== "All Modules")
      f.push({
        key: "module",
        label: filterModule,
        clear: () => setFMod("All Modules"),
      });
    if (filterSev !== "All Severity")
      f.push({
        key: "sev",
        label: SEVERITY_LABEL[filterSev] || filterSev,
        clear: () => setFSev("All Severity"),
      });
    if (filterSource !== "All Sources")
      f.push({
        key: "source",
        label: filterSource,
        clear: () => setFSrc("All Sources"),
      });
    if (filterAction !== "All Actions")
      f.push({
        key: "action",
        label: filterAction,
        clear: () => setFAct("All Actions"),
      });
    if (dateFrom)
      f.push({
        key: "from",
        label: `From ${dateFrom}`,
        clear: () => setDateFrom(""),
      });
    if (dateTo)
      f.push({ key: "to", label: `To ${dateTo}`, clear: () => setDateTo("") });
    return f;
  }, [filterModule, filterSev, filterSource, filterAction, dateFrom, dateTo]);

  // ── Client-side filter + sort (on current page data) ──────────────────────
  const filtered = useMemo(() => {
    let list = [...events];
    if (search.trim().length >= 1) {
      const q = search.toLowerCase();
      list = list.filter(
        (e) =>
          e.actor.toLowerCase().includes(q) ||
          e.action.toLowerCase().includes(q) ||
          e.target.toLowerCase().includes(q) ||
          e.module.toLowerCase().includes(q) ||
          e.id.toLowerCase().includes(q),
      );
    }
    if (filterModule !== "All Modules")
      list = list.filter((e) => e.module === filterModule);
    if (filterSev !== "All Severity")
      list = list.filter((e) => e.severity === filterSev);
    if (filterSource !== "All Sources")
      list = list.filter((e) => e.source === filterSource);
    if (filterAction !== "All Actions")
      list = list.filter((e) => e.action === filterAction);
    if (dateFrom) list = list.filter((e) => e.timestamp >= dateFrom);
    if (dateTo) list = list.filter((e) => e.timestamp <= dateTo + "T23:59:59");

    list.sort((a, b) => {
      const aV = (a[sortKey] || "").toString();
      const bV = (b[sortKey] || "").toString();
      return sortDir === "asc" ? aV.localeCompare(bV) : bV.localeCompare(aV);
    });
    return list;
  }, [
    events,
    search,
    filterModule,
    filterSev,
    filterSource,
    filterAction,
    dateFrom,
    dateTo,
    sortKey,
    sortDir,
  ]);

  // Server-side pagination: totalPages based on API total
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const clearAll = () => {
    setFMod("All Modules");
    setFSev("All Severity");
    setFSrc("All Sources");
    setFAct("All Actions");
    setDateFrom("");
    setDateTo("");
    setSearch("");
    setPage(1);
  };

  const handleCopy = useCallback((text) => {
    setCopyToast(`Copied: ${text}`);
    setTimeout(() => setCopyToast(null), 2000);
  }, []);

  const SortTh = ({ col, label }) => (
    <th
      className={sortKey === col ? "sorted" : ""}
      onClick={() => handleSort(col)}
    >
      {label}
      <span className={`sort-icon${sortKey === col ? " active" : ""}`}>
        {Ico.SortUp}
      </span>
    </th>
  );

  return (
    <div className="al-page">
      {/* ── Header ── */}
      <div className="al-header">
        <div className="al-header__left">
          <span className="al-header__label">Compliance</span>
          <h1 className="al-header__title">Audit Logs</h1>
          <p className="al-header__subtitle">
            Search and export administrative and messaging metadata events for
            compliance
          </p>
        </div>
        <div className="al-header__right">
          <div className="al-live">
            <div className="al-live__dot" /> Live
          </div>
          <button
            className="al-btn al-btn--secondary"
            onClick={() => alert("Exporting CSV…")}
          >
            {Ico.Export} Export CSV
          </button>
          <button
            className="al-btn al-btn--primary"
            onClick={() => alert("Exporting PDF…")}
          >
            {Ico.Export} Export PDF
          </button>
        </div>
      </div>

      {/* ── KPIs ── */}
      <div className="al-kpi-grid">
        {[
          {
            label: "Total Events",
            value: stats.total,
            trend: "+124",
            dir: "up",
            color: "#0062FE",
            bg: "rgba(0,98,254,0.10)",
            icon: Ico.Activity,
          },
          {
            label: "Critical Events",
            value: stats.critical,
            trend: "+2",
            dir: "down",
            color: "#EF4444",
            bg: "rgba(239,68,68,0.10)",
            icon: Ico.AlertTri,
          },
          {
            label: "Warnings",
            value: stats.warning,
            trend: "+5",
            dir: "down",
            color: "#F97316",
            bg: "rgba(249,115,22,0.10)",
            icon: Ico.Shield,
          },
          {
            label: "Modules Covered",
            value: stats.modules,
            trend: "0",
            dir: "flat",
            color: "#8B5CF6",
            bg: "rgba(139,92,246,0.10)",
            icon: Ico.Lock,
          },
        ].map((k) => (
          <div key={k.label} className="al-kpi">
            <div
              className="al-kpi__icon"
              style={{ background: k.bg, color: k.color }}
            >
              {k.icon}
            </div>
            <div className="al-kpi__body">
              <div className="al-kpi__value">
                {loading ? (
                  <div
                    className="al-skeleton"
                    style={{ width: 32, height: 20, display: "inline-block" }}
                  />
                ) : (
                  k.value
                )}
              </div>
              <div className="al-kpi__label">{k.label}</div>
            </div>
            <span className={`al-kpi__trend al-kpi__trend--${k.dir}`}>
              {k.trend}
            </span>
          </div>
        ))}
      </div>

      {/* ── Filter Panel ── */}
      <div className="al-filter-panel">
        <div className="al-filter-panel__top">
          <div className="al-search-wrap">
            {Ico.Search}
            <input
              className="al-search"
              placeholder="Search by actor, action, entity ID…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <button
            className="al-btn al-btn--secondary"
            onClick={fetchLogs}
            disabled={loading}
          >
            {loading ? Ico.Loader : Ico.Refresh} Refresh
          </button>
          <button
            className={`al-filter-panel__toggle${showAdvanced ? " open" : ""}`}
            onClick={() => setShowAdv((v) => !v)}
          >
            {Ico.Filter} Advanced Filters {Ico.ChevD}
          </button>
        </div>

        {showAdvanced && (
          <div className="al-filter-panel__advanced">
            <div>
              <span className="al-filter-label">Module</span>
              <select
                className="al-filter-select"
                value={filterModule}
                onChange={(e) => {
                  setFMod(e.target.value);
                  setPage(1);
                }}
              >
                {MODULES_OPT.map((m) => (
                  <option key={m}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <span className="al-filter-label">Severity</span>
              <select
                className="al-filter-select"
                value={filterSev}
                onChange={(e) => {
                  setFSev(e.target.value);
                  setPage(1);
                }}
              >
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {s === "All Severity" ? s : SEVERITY_LABEL[s]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <span className="al-filter-label">Source</span>
              <select
                className="al-filter-select"
                value={filterSource}
                onChange={(e) => {
                  setFSrc(e.target.value);
                  setPage(1);
                }}
              >
                {SOURCES.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <span className="al-filter-label">Action</span>
              <select
                className="al-filter-select"
                value={filterAction}
                onChange={(e) => {
                  setFAct(e.target.value);
                  setPage(1);
                }}
              >
                {ACTIONS_OPT.map((a) => (
                  <option key={a}>{a}</option>
                ))}
              </select>
            </div>
            <div>
              <span className="al-filter-label">Date From</span>
              <input
                className="al-date-input"
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div>
              <span className="al-filter-label">Date To</span>
              <input
                className="al-date-input"
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>
        )}

        {activeFilters.length > 0 && (
          <div className="al-filter-panel__active-filters">
            <span style={{ fontSize: 12, color: "#6B7280", fontWeight: 600 }}>
              Active:
            </span>
            {activeFilters.map((f) => (
              <span key={f.key} className="al-filter-chip">
                {f.label}
                <button onClick={f.clear}>{Ico.XCircle}</button>
              </span>
            ))}
            <button
              className="al-btn al-btn--ghost"
              style={{ padding: "3px 8px", fontSize: 12 }}
              onClick={clearAll}
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* ── Error Banner ── */}
      {apiError && (
        <div className="al-error-banner">
          {Ico.AlertTri}
          <span>{apiError}</span>
          <button className="al-btn al-btn--ghost" onClick={fetchLogs}>
            {Ico.Refresh} Retry
          </button>
        </div>
      )}

      {/* ── Table ── */}
      <div className="al-card">
        <div className="al-table-wrap">
          {!loading && !apiError && filtered.length === 0 ? (
            <div className="al-empty">
              {Ico.Shield}
              <h4>No audit events found</h4>
              <p>Try adjusting your search or filters.</p>
              <button className="al-btn al-btn--secondary" onClick={clearAll}>
                Clear Filters
              </button>
            </div>
          ) : (
            <table className="al-table">
              <thead>
                <tr>
                  <SortTh col="id" label="Event ID" />
                  <SortTh col="timestamp" label="Time" />
                  <SortTh col="actor" label="Actor" />
                  <SortTh col="action" label="Action" />
                  <SortTh col="target" label="Entity ID" />
                  <SortTh col="module" label="Module" />
                  <SortTh col="severity" label="Severity" />
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <SkeletonRows count={pageSize > 10 ? 10 : pageSize} />
                ) : (
                  filtered.map((ev) => {
                    const mod = MODULE_MAP[ev.module] || MODULE_MAP.System;
                    const sev = ev.severity;
                    const actionColor =
                      {
                        info: "#0062FE",
                        success: "#22C55E",
                        warning: "#F97316",
                        critical: "#EF4444",
                      }[sev] || "#6B7280";
                    return (
                      <tr
                        key={ev.id}
                        className={sev === "critical" ? "highlighted" : ""}
                        onClick={() => setDetail(ev)}
                      >
                        <td
                          style={{
                            fontFamily: "monospace",
                            fontSize: 12,
                            color: "#0062FE",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {ev.id.length > 20
                            ? ev.id.slice(0, 8) + "…" + ev.id.slice(-4)
                            : ev.id}
                        </td>
                        <td
                          style={{
                            fontSize: 12.5,
                            color: "#6B7280",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {ev.time}
                        </td>
                        <td>
                          <div className="al-actor-cell">
                            <div
                              className="al-actor-cell__avatar"
                              style={{ background: getActorColor(ev.actor) }}
                            >
                              {initials(ev.actor)}
                            </div>
                            <div>
                              <div className="al-actor-cell__name">
                                {ev.actor}
                              </div>
                              <div className="al-actor-cell__role">
                                {ev.actorRole}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="al-action-cell">
                            <div
                              className="al-action-cell__dot"
                              style={{ background: actionColor }}
                            />
                            <span className="al-action-cell__label">
                              {ev.action}
                            </span>
                          </div>
                        </td>
                        <td
                          style={{
                            maxWidth: 140,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: 12,
                            fontFamily: "monospace",
                            color: "#6B7280",
                          }}
                        >
                          {ev.target}
                        </td>
                        <td>
                          <span
                            className="al-module"
                            style={{ background: mod.bg, color: mod.color }}
                          >
                            {ev.module}
                          </span>
                        </td>
                        <td>
                          <span className={`al-severity al-severity--${sev}`}>
                            {SEVERITY_LABEL[sev]}
                          </span>
                        </td>
                        <td>
                          <span className="al-source">{ev.sourceIp}</span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        <div className="al-pagination">
          <div className="al-pagination__info">
            {loading ? (
              <span>Loading…</span>
            ) : (
              <>
                Showing {Math.min((page - 1) * pageSize + 1, totalCount)}–
                {Math.min(page * pageSize, totalCount)} of {totalCount} events
                &nbsp;·&nbsp;
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {PAGE_SIZE_OPTS.map((n) => (
                    <option key={n}>{n}</option>
                  ))}
                </select>{" "}
                per page
              </>
            )}
          </div>
          <div className="al-pagination__controls">
            <button
              className="al-pagination__page"
              disabled={page === 1 || loading}
              onClick={() => setPage(1)}
            >
              &laquo;
            </button>
            <button
              className="al-pagination__page"
              disabled={page === 1 || loading}
              onClick={() => setPage((p) => p - 1)}
            >
              {Ico.ChevR}
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
              return (
                <button
                  key={p}
                  className={`al-pagination__page${page === p ? " active" : ""}`}
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              );
            })}
            <button
              className="al-pagination__page"
              disabled={page === totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              {Ico.ChevR}
            </button>
            <button
              className="al-pagination__page"
              disabled={page === totalPages || loading}
              onClick={() => setPage(totalPages)}
            >
              &raquo;
            </button>
          </div>
        </div>
      </div>

      {/* ── Detail Drawer ── */}
      {detail && (
        <DetailDrawer
          event={detail}
          onClose={() => setDetail(null)}
          onCopy={handleCopy}
        />
      )}

      {/* ── Copy Toast ── */}
      {copyToast && <div className="al-copy-toast">✓ {copyToast}</div>}
    </div>
  );
};

export default Auditlog;
